import { BonusEntry, RoundScoreDetail, RoundVariant } from '../types/game.types';

export const COMPLETE_RULES_DECK_SIZE = 74;

export const ROUND_VARIANTS: Record<RoundVariant, { label: string; description: string; plan: number[] }> = {
  classic: {
    label: 'Classique',
    description: 'Dix manches, de 1 à 10 cartes.',
    plan: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  },
  evenKeeled: {
    label: 'Even Keeled',
    description: 'Cinq manches de 2, 4, 6, 8 et 10 cartes.',
    plan: [2, 4, 6, 8, 10]
  },
  skip: {
    label: 'Skip',
    description: 'Les cinq dernières manches seulement, de 6 à 10 cartes.',
    plan: [6, 7, 8, 9, 10]
  },
  fiveOfFive: {
    label: 'Cinq par cinq',
    description: 'Cinq manches de cinq cartes.',
    plan: [5, 5, 5, 5, 5]
  },
  tenOfTen: {
    label: 'Dix par dix',
    description: 'Dix manches de dix cartes.',
    plan: [10, 10, 10, 10, 10, 10, 10, 10, 10, 10]
  },
  whirlpool: {
    label: 'Whirlpool',
    description: 'Neuf, sept, cinq, trois, un, puis le chemin inverse.',
    plan: [9, 7, 5, 3, 1, 1, 3, 5, 7, 9]
  },
  single: {
    label: 'Une manche',
    description: 'Une seule manche d’une carte.',
    plan: [1]
  }
};

export function getRoundPlan(variant: RoundVariant): number[] {
  return [...ROUND_VARIANTS[variant].plan];
}

export function getCardsDealtForRound(requestedCards: number, playerCount: number): number {
  const effectiveSeats = playerCount === 2 ? 3 : playerCount;
  const deckLimit = Math.floor(COMPLETE_RULES_DECK_SIZE / effectiveSeats);
  const officialEightPlayerLimit = playerCount === 8 ? 8 : requestedCards;
  return Math.max(1, Math.min(requestedCards, officialEightPlayerLimit, deckLimit));
}

export function calculateBaseRoundScore(prediction: number, result: number, cardsDealt: number): number {
  if (prediction === result) {
    return prediction === 0 ? cardsDealt * 10 : prediction * 20;
  }

  if (prediction === 0) return -(cardsDealt * 10);
  return -Math.abs(prediction - result) * 10;
}

export function calculateRascalRoundScore(prediction: number, result: number, cardsDealt: number): number {
  const potential = cardsDealt * 10;
  const difference = Math.abs(prediction - result);
  if (difference === 0) return potential;
  if (difference === 1) return potential / 2;
  return 0;
}

function isCaptureBonus(bonus: BonusEntry): boolean {
  return bonus.kind !== 'rascal-wager' && bonus.kind !== 'manual-adjustment';
}

export function calculateAppliedBonuses(
  bonuses: BonusEntry[],
  prediction: number,
  result: number,
  rascalScoring: boolean
): BonusEntry[] {
  const exactBid = prediction === result;
  const difference = Math.abs(prediction - result);
  const multiplier = rascalScoring ? (difference === 0 ? 1 : difference === 1 ? 0.5 : 0) : (exactBid ? 1 : 0);

  return bonuses
    .map(bonus => {
      if (!isCaptureBonus(bonus)) return bonus;
      if (bonus.kind === 'loot-alliance' && !exactBid) return { ...bonus, points: 0 };
      const points = bonus.points * multiplier;
      return {
        ...bonus,
        points,
        label: rascalScoring && multiplier === 0.5 ? `${bonus.label} à moitié` : bonus.label
      };
    })
    .filter(bonus => bonus.points !== 0);
}

export function createRoundDetail(
  round: number,
  cardsDealt: number,
  prediction: number,
  result: number,
  bonuses: BonusEntry[] = [],
  bidPointsOverride?: number,
  scoringMethod: 'standard' | 'rascal' = 'standard'
): RoundScoreDetail {
  const bidPoints = bidPointsOverride ?? calculateBaseRoundScore(prediction, result, cardsDealt);
  const bonusPoints = bonuses.reduce((total, bonus) => total + bonus.points, 0);

  return {
    round,
    cardsDealt,
    prediction,
    result,
    bidPoints,
    bonusPoints,
    totalPoints: bidPoints + bonusPoints,
    bonuses,
    scoringMethod
  };
}

export function createGameId(): string {
  return `sk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}
